export interface ApplicationDateContext {
  local_today: string;
  timezone: string;
}

interface AnchoredApplicationDateContext extends ApplicationDateContext {
  dateOffsetDays: number;
}

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

let applicationDateContext: AnchoredApplicationDateContext | null = null;

const isValidISODate = (value: unknown): value is string => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === month - 1
    && parsed.getUTCDate() === day;
};

const isoDateToEpochDay = (value: string): number => {
  const [year, month, day] = value.split('-').map(Number);
  return Math.floor(Date.UTC(year, month - 1, day) / MILLISECONDS_PER_DAY);
};

const epochDayToISODate = (epochDay: number): string =>
  new Date(epochDay * MILLISECONDS_PER_DAY).toISOString().slice(0, 10);

const dateInTimezone = (input: Date, timezone: string): string | null => {
  if (Number.isNaN(input.getTime())) return null;

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      calendar: 'gregory',
      numberingSystem: 'latn',
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(input);
    const valueFor = (type: Intl.DateTimeFormatPartTypes): string | undefined =>
      parts.find((part) => part.type === type)?.value;
    const value = `${valueFor('year')}-${valueFor('month')}-${valueFor('day')}`;

    return isValidISODate(value) ? value : null;
  } catch {
    return null;
  }
};

export const localDateISO = (input: Date = new Date()): string => {
  const year = input.getFullYear();
  const month = String(input.getMonth() + 1).padStart(2, '0');
  const day = String(input.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const configureApplicationDateContext = (
  context?: Partial<ApplicationDateContext> | null,
  receivedAt: Date = new Date()
): boolean => {
  const localToday = context?.local_today;
  const timezone = typeof context?.timezone === 'string' ? context.timezone.trim() : '';
  const clientDateAtReceipt = timezone ? dateInTimezone(receivedAt, timezone) : null;

  if (!isValidISODate(localToday)
      || !clientDateAtReceipt) {
    applicationDateContext = null;
    return false;
  }

  applicationDateContext = {
    local_today: localToday,
    timezone,
    dateOffsetDays: isoDateToEpochDay(localToday) - isoDateToEpochDay(clientDateAtReceipt),
  };
  return true;
};

export const clearApplicationDateContext = (): void => {
  applicationDateContext = null;
};

export const getApplicationDateContext = (): ApplicationDateContext | null =>
  applicationDateContext
    ? {
      local_today: applicationDateContext.local_today,
      timezone: applicationDateContext.timezone,
    }
    : null;

export const todayISO = (input: Date = new Date()): string => {
  if (!applicationDateContext) return localDateISO(input);

  const zonedDate = dateInTimezone(input, applicationDateContext.timezone);
  if (!zonedDate) return applicationDateContext.local_today;

  return epochDayToISODate(
    isoDateToEpochDay(zonedDate) + applicationDateContext.dateOffsetDays
  );
};

export const millisecondsUntilApplicationDateRollover = (
  input: Date = new Date()
): number | null => {
  if (!applicationDateContext || Number.isNaN(input.getTime())) return null;

  const start = Math.floor(input.getTime());
  const currentDate = todayISO(input);
  let lowerBound = start;
  let upperBound = start + (2 * MILLISECONDS_PER_DAY);

  if (todayISO(new Date(upperBound)) === currentDate) return null;

  // Find the first millisecond whose calendar date differs in the authoritative
  // timezone. This remains correct across daylight-saving transitions.
  while (upperBound - lowerBound > 1) {
    const midpoint = lowerBound + Math.floor((upperBound - lowerBound) / 2);
    if (todayISO(new Date(midpoint)) === currentDate) {
      lowerBound = midpoint;
    } else {
      upperBound = midpoint;
    }
  }

  return Math.max(1, upperBound - start);
};

export const applicationTimezone = (): string =>
  applicationDateContext?.timezone
  || Intl.DateTimeFormat().resolvedOptions().timeZone
  || 'UTC';

export const isFutureDate = (value?: string | null): boolean =>
  Boolean(value && value > todayISO());

export const isPastDate = (value?: string | null): boolean =>
  Boolean(value && value < todayISO());
